import { useState, useEffect, useCallback } from 'react';
import { useAppointmentsService } from '@/data/services/useAppointmentsService';
import { getLocale } from '@/utils/formatedLanguage';
import { compareAsc, format } from 'date-fns';
import { useSnackbarStoreComponent } from '@/stores/useSnackbar';
import { PageBase } from '../PageBase';
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  Grid2,
  Badge,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTranslation } from 'react-i18next';
import { AppointmentCard } from './components/AppointmentCard';
import { theme } from '@/styles/theme';
import { useNavigate } from 'react-router-dom';
import { FilterModal } from './components/FilterModal';

import EmptyFilter from '@/assets/empty_filter.svg';
import Button from '@/components/button/Button';
import { capitalizeFirstLetterOfEachWord } from '@/utils/getInitials';
import { useAuthStore } from '@/stores/AuthStore';
import { dateFormatter } from '@/utils/dateFormatter';

interface Appointment {
  id: string;
  patientName: string;
  avatar: string;
  date: string;
  time: string;
}

export const Appointments = () => {
  const { getAppointmentsCardList } = useAppointmentsService();
  const { t } = useTranslation('appointment');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const [visibleCount, setVisibleCount] = useState<number>(4);
  const { showSnackbar } = useSnackbarStoreComponent();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const userFullName = `${user?.name || ''} ${user?.surname || ''}`;
  const { getUserLocalTimeOrClinicTime } = dateFormatter();

  const processAppointmentData = useCallback(
    (item: any): Appointment => {
      const locale = getLocale();
      const parsedDate = getUserLocalTimeOrClinicTime(item.date, item.time, item.location.gmt);

      const dateFormatted = item.date
        ? format(parsedDate.asDate, 'PPP', { locale })
        : t('Data não informada');
      const timeFormatted = item.time
        ? format(parsedDate.asDate, 'hh:mm a', { locale })
        : t('Hora não informada');

        //const timeDifference = calculateTimeDifference(item.location.gmt);

      return {
        id: item.id,
        patientName: item.dependent?.name
          ? capitalizeFirstLetterOfEachWord(
              `${item.dependent.name} ${item.dependent.surname}`
            )
          : capitalizeFirstLetterOfEachWord(userFullName) ||
            'Nome do paciente não informado',
        avatar: item?.dependent
          ? item?.dependent?.profilePhotoUrl
            ? item?.dependent?.profilePhotoUrl
            : ''
          : user?.picture
          ? user.picture
          : '',
        date: dateFormatted,
        time: `${timeFormatted} ${parsedDate.timeZone}`,
      };
    },
    [t]
  );

  const fetchAppointments = useCallback(
    (filter?: 'all' | 'mine' | 'dependents') => {
      setLoading(true);
      const mine = filter && filter !== 'all' ? filter === 'mine' : undefined;

      getAppointmentsCardList(mine)
        .then((data) => {
          const processedData = data
            .map(processAppointmentData)
            .filter((item) => item.date !== t('Data não informada'))
            .sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)));

          setAppointments(processedData);
        })
        .catch((err) => {
          showSnackbar(
            t(err.message || 'Erro ao buscar agendamentos'),
            'error'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [getAppointmentsCardList, processAppointmentData, showSnackbar, t]
  );

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const [filterCount, setFilterCount] = useState(0);

  const applyFilter = (filter?: 'all' | 'mine' | 'dependents') => {
    fetchAppointments(filter);
    handleClose();
    setFilterCount(filter === 'all' ? 0 : 1);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 2);
  };

  return (
    <PageBase
      title={t('next_appointments')}
      onBackClick={() => navigate(-1)}
      paddingInline={2}
    >
      <Box sx={styles.container}>
        <Typography sx={{ fontSize: '1.8rem' }}>
          {t('all_appointments')}
        </Typography>
        <Box
          sx={{
            borderRadius: 8,
            backgroundColor:
              filterCount > 0 ? theme.palette.primary.light : 'transparent',
          }}
        >
          <Badge
            color="error"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            badgeContent={filterCount > 0 ? filterCount : null}
          >
            <IconButton onClick={handleClickOpen}>
              <FilterAltOutlinedIcon
                sx={{ color: theme.palette.primary.main, fontSize: '2rem' }}
              />
            </IconButton>
          </Badge>
        </Box>
      </Box>
      {loading ? (
        <Box>
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={118}
              sx={{ borderRadius: 2, mb: 2 }}
            />
          ))}
        </Box>
      ) : appointments.length > 0 ? (
        <Grid2
          columnGap={2}
          rowGap={2}
          container
          columns={{ xs: 1, sm: 2, md: 2 }}
        >
          {appointments.slice(0, visibleCount).map((appointment) => (
            <AppointmentCard
              key={appointment.id}
              onViewDetails={() => navigate(`/appointment/${appointment.id}`)}
              appointment={appointment}
            />
          ))}
        </Grid2>
      ) : (
        <Box sx={styles.empty}>
          <img src={EmptyFilter} alt="Image empty" />

          <Typography sx={{ textAlign: 'center', mt: 4 }}>
            {t('empty_filter')}
          </Typography>
        </Box>
      )}
      {!loading && visibleCount < appointments.length && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button onlyText title={t('load_more')} onClick={handleLoadMore} />
        </Box>
      )}
      <FilterModal open={open} handleClose={handleClose} filter={applyFilter} />
    </PageBase>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    mb: 2,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
